import { useMemo, useRef, useEffect } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import Table from '~/shared/components/Table';
import Typography from '~/shared/components/Typography';
import { useXFetch } from '~/shared/utils/useXFetch';
import {
  OptLogVolumeActionsCountResponse,
  OptLogVolumeActionsResponse,
  OptLogVolumeActions,
} from '~/shared/types/api';
import { TABLE_LIVE_REFRESH_MS } from '~/shared/constants/polling';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { isOrganizationView } from '~/shared/utils/accountUtils';
import { generateSkeletonRows, createSkeletonAction } from '~/shared/utils/skeletonUtils';
import { ActionTypeFilterValue } from '~/shared/components/filters/ActionTypeDropdown';
import { usePermission } from '~/shared/hooks/usePermission';
import {
  isSkeletonActionRow,
  useOptimizationActionsRowData,
  type ActionTableRow,
} from '~/shared/hooks/useOptimizationActionsRowData';

import { shouldShowOptLogActionsTableLoader } from '~/shared/utils/optLogActionsTableLoader';
import { useOptLogActionsTableGridProps } from '~/shared/hooks/useOptLogActionsTableGridProps';
import {
  extractOptLogActionsFromResponse,
  getOptLogActionGridRowId,
  getOptLogActionRowClass,
  normalizeOptLogActions,
} from '~/shared/utils/optLogActionUtils';

import { getOperationColumnDefs } from '../../ReportTable/columnDefs';

import OptimizationActions from './OptimizationActions';

interface OptimizationTableQueryResult {
  actions: (OptLogVolumeActions & { _isSkeleton?: boolean })[];
  count: number;
}

type OptimizationTableRow = ActionTableRow;

interface OptimizationTableProps {
  tableQueryUrl: string;
  countQueryUrl: string;
  onPageChange: (page: number) => void;
  onSortChange: (sort: { field: string; dir: 'desc' | 'asc' }) => void;
  dataLimit: number;
  currentSortField: { field: string; dir: 'desc' | 'asc' };
  currentOffset: string;
  timeSignature: string;
  filtersSignature: string;
  onCountChange?: (count: number) => void;
  selectedActionType: ActionTypeFilterValue;
  onActionTypeChange: (type: ActionTypeFilterValue) => void;
  typeCounts?: Partial<Record<ActionTypeFilterValue, number>>;
  typeCountsLoading?: boolean;
  allActionsCount?: number;
  allActionsCountLoading?: boolean;
}

export default function OptimizationTable({
  tableQueryUrl,
  countQueryUrl,
  onPageChange,
  onSortChange,
  dataLimit,
  currentSortField,
  currentOffset,
  timeSignature,
  filtersSignature,
  onCountChange,
  selectedActionType,
  onActionTypeChange,
  typeCounts,
  typeCountsLoading,
  allActionsCount,
  allActionsCountLoading,
}: OptimizationTableProps) {
  const api = useXFetch();
  const { accounts, activeTenantId } = useAuthContext();

  const isOrgView = isOrganizationView(activeTenantId, accounts);
  const canReadUsers = usePermission('datafy.read.users');

  const { data, isFetching, isPlaceholderData, isLoading, isError, error } =
    useQuery<OptimizationTableQueryResult>(
    {
      queryKey: [
        'getOptimizationActions',
        tableQueryUrl,
        countQueryUrl,
        currentOffset,
        timeSignature,
        filtersSignature,
        selectedActionType,
        dataLimit,
      ],
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
      enabled: !!tableQueryUrl && !!countQueryUrl,
      refetchInterval: TABLE_LIVE_REFRESH_MS,
      queryFn: async () => {
        const actionsResponse = await api<OptLogVolumeActionsResponse & Record<string, unknown>>(
          tableQueryUrl
        );
        const countResponse = await api<OptLogVolumeActionsCountResponse>(countQueryUrl);
        const actions = normalizeOptLogActions(extractOptLogActionsFromResponse(actionsResponse));
        const count = countResponse.count ?? 0;

        return { actions, count };
      },
    }
  );

  const operationColumnDefs = useMemo(
    () => getOperationColumnDefs({ sortField: currentSortField, isOrgView, accounts }),
    [currentSortField, isOrgView, accounts]
  );
  const showLoader = shouldShowOptLogActionsTableLoader({
    isLoading,
    isFetching,
    isPlaceholderData,
    hasQueryData: data != null,
  });

  const baseRowData = useMemo((): OptimizationTableRow[] => {
    if (showLoader) {
      return generateSkeletonRows(dataLimit, createSkeletonAction()) as OptimizationTableRow[];
    }
    return (data as unknown as OptimizationTableQueryResult)?.actions || [];
  }, [(data as unknown as OptimizationTableQueryResult)?.actions, showLoader, dataLimit]);

  const displayRowData = useOptimizationActionsRowData({
    tableQueryUrl,
    baseRowData,
    canReadUsers,
    showLoader,
  });

  const optLogTableGridProps = useOptLogActionsTableGridProps(displayRowData);
  const lastKnownCountRef = useRef<number | undefined>(undefined);
  if ((data as unknown as OptimizationTableQueryResult)?.count !== undefined) {
    lastKnownCountRef.current = (data as unknown as OptimizationTableQueryResult).count;
  }
  const stableCount = lastKnownCountRef.current ?? 0;

  const dataCount = (data as unknown as OptimizationTableQueryResult)?.count;
  useEffect(() => {
    if (onCountChange !== undefined && dataCount !== undefined) {
      onCountChange(dataCount);
    }
  }, [dataCount, onCountChange]);

  const currentPage = useMemo(
    () => Math.floor((parseInt(currentOffset, 10) || 0) / dataLimit),
    [currentOffset, dataLimit]
  );

  return (
    <div className="flex flex-col pt-5">
      <div>
        <OptimizationActions
          selectedActionType={selectedActionType}
          onActionTypeChange={onActionTypeChange}
          typeCounts={typeCounts}
          typeCountsLoading={typeCountsLoading}
          allActionsCount={allActionsCount}
          allActionsCountLoading={allActionsCountLoading}
        />
      </div>

      {isError && (
        <Typography variant="p3" className="text-red-600 pb-2">
          Failed to load actions
          {error instanceof Error && error.message ? `: ${error.message}` : ''}
        </Typography>
      )}
      <div className="opt-log-actions-table flex flex-col pt-2.5 pb-[14px] h-[520px] 2xl:h-[702px] ">
        <Table
          {...optLogTableGridProps}
          rowData={displayRowData}
          columnDefs={operationColumnDefs}
          defaultColDef={{ filter: false }}
          headerHeight={48}
          getRowClass={(row) =>
            isSkeletonActionRow(row) ? '' : getOptLogActionRowClass(row)
          }
          onSortChange={(sort) => {
            // Prevent sorting when loading skeleton data
            if (
              showLoader &&
              (!(data as unknown as OptimizationTableQueryResult)?.actions ||
                (data as unknown as OptimizationTableQueryResult).actions.length === 0)
            ) {
              return;
            }
            onSortChange(sort);
          }}
          itemsName={'actions'}
          noResultsText="No Results"
          pagination={{
            dataCount: stableCount,
            perPage: dataLimit,
            onChange: onPageChange,
            totalPages: Math.ceil(stableCount / dataLimit) || 1,
            currentPage,
          }}
          loading={false}
          suppressNoRowsWhileLoading={true}
          getRowId={(params) => {
            if (isSkeletonActionRow(params.data)) {
              return params.data.volumeId;
            }
            return getOptLogActionGridRowId(params.data);
          }}
        />
      </div>
    </div>
  );
}
